import React, { useEffect, useState } from "react";
import {
  readRecentAchievements,
  ACHIEVEMENTS_UPDATED_EVENT,
} from "@/lib/celebrate";
import { Sparkles } from "lucide-react";

const REASON_META = {
  course_passed: { color: "#F4B81F", label: "Course passed" },
  points_earned: { color: "#FBBF24", label: "Points earned" },
  redeem_reward: { color: "#7C3AED", label: "Reward redeemed" },
  book_completed: { color: "#16A34A", label: "Book completed" },
  default: { color: "#F4B81F", label: "Achievement" },
};

function getMeta(reason) {
  return REASON_META[reason] || REASON_META.default;
}

function timeAgo(ts) {
  if (!ts) return "";
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Compact "last 5 achievements" strip — 5 colored dots, newest on the left.
 * Empty slots render as a faint gray ring so the rhythm stays predictable.
 *
 * Reads from localStorage and refreshes when:
 *   - The window dispatches `canales:achievements:updated`.
 *   - Another tab updates `canales_recent_achievements_v1` (storage event).
 */
export default function RecentAchievements() {
  const [items, setItems] = useState(() => readRecentAchievements());

  useEffect(() => {
    const refresh = () => setItems(readRecentAchievements());
    window.addEventListener(ACHIEVEMENTS_UPDATED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(ACHIEVEMENTS_UPDATED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const slots = Array.from({ length: 5 }, (_, i) => items[i]);
  const hasAny = items.length > 0;

  return (
    <div
      className="inline-flex items-center gap-3 px-3.5 py-2 rounded-full bg-white border border-[#D1D5DB]"
      data-testid="recent-achievements"
    >
      <Sparkles className="h-3.5 w-3.5 text-[#F4B81F]" strokeWidth={2} />
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-medium">
        Recent wins
      </span>
      <div className="flex items-center gap-1.5">
        {slots.map((entry, idx) => {
          if (!entry) {
            return (
              <span
                key={`empty-${idx}`}
                aria-hidden
                className="h-2.5 w-2.5 rounded-full border border-[#E5E7EB]"
                data-testid={`achievement-slot-${idx}`}
              />
            );
          }
          const meta = getMeta(entry.reason);
          return (
            <span
              key={`a-${idx}-${entry.t}`}
              className="h-2.5 w-2.5 rounded-full shadow-sm transition-transform hover:scale-125"
              style={{ backgroundColor: meta.color }}
              title={`${meta.label} · ${timeAgo(entry.t)}`}
              data-testid={`achievement-slot-${idx}`}
              data-reason={entry.reason}
            />
          );
        })}
      </div>
      {!hasAny && (
        <span className="text-[10px] text-[#9CA3AF]">No wins yet</span>
      )}
    </div>
  );
}
